import React from "react";

/**
 * Side-by-side preview of 3 palette directions for the marketing site.
 * Each column renders a mini hero + metric row + CTA + service card using the
 * copy from soverenxsystems.com.au so Pankaj can judge the vibe directly.
 *
 * Route: /brand-preview (no auth needed — pure visual)
 */

const palettes = [
  {
    id: "a",
    name: "A · Defence-Grade",
    tag: "Charcoal + Olive + Bronze",
    tagline: "Government-adjacent · NV-cleared · heritage trust",
    primary: "#1C1F1A",
    secondary: "#4A5240",
    accent: "#C58B3D",
    surface: "#F5F2EC",
    textOnSurface: "#1C1F1A",
    textOnDark: "#F5F2EC",
    muted: "#8B8B82",
  },
  {
    id: "b",
    name: "B · Industrial Signal",
    tag: "Graphite + Orange",
    tagline: "Field-ready · Australian industrial · active energy",
    primary: "#1F2328",
    secondary: "#3C4249",
    accent: "#F26B1F",
    surface: "#FFFFFF",
    textOnSurface: "#1F2328",
    textOnDark: "#FFFFFF",
    muted: "#6B7280",
  },
  {
    id: "c",
    name: "C · Secure Premium",
    tag: "Deep Teal + Copper",
    tagline: "Security + gravitas · still cool but not blue",
    primary: "#0E3A3A",
    secondary: "#1E5555",
    accent: "#B87333",
    surface: "#FBFAF7",
    textOnSurface: "#0E3A3A",
    textOnDark: "#FBFAF7",
    muted: "#8A8F8A",
  },
];

function MiniSite({ p }) {
  return (
    <div className="rounded-lg overflow-hidden shadow-lg border border-slate-200" style={{ background: p.surface, color: p.textOnSurface }}>
      {/* Nav */}
      <div className="px-5 py-3 flex items-center justify-between" style={{ background: p.primary }}>
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-sm" style={{ background: p.accent }} />
          <span className="font-bold tracking-tight" style={{ color: p.textOnDark, fontFamily: "'Space Grotesk', sans-serif" }}>SoverenX</span>
        </div>
        <div className="flex gap-4 text-[10px] uppercase tracking-wider" style={{ color: p.textOnDark, opacity: 0.8 }}>
          <span>Services</span><span>Vendors</span><span>About</span>
          <span className="px-2 py-0.5 rounded" style={{ background: p.accent, color: p.primary, opacity: 1 }}>Contact</span>
        </div>
      </div>

      {/* Hero */}
      <div className="px-6 py-10" style={{ background: p.primary }}>
        <div className="text-[10px] uppercase tracking-[0.25em] mb-3" style={{ color: p.accent }}>Enterprise Network Specialists — Australia</div>
        <h1 className="text-3xl font-bold leading-tight mb-3" style={{ color: p.textOnDark, fontFamily: "'Space Grotesk', sans-serif" }}>
          Channel-First.<br />Partner-Only.<br /><span style={{ color: p.accent }}>Professional Services.</span>
        </h1>
        <p className="text-sm leading-relaxed max-w-md mb-5" style={{ color: p.textOnDark, opacity: 0.75 }}>
          Dedicated deployment, cloud architecture, and security services for enterprise network infrastructure. We deliver on your behalf.
        </p>
        <div className="flex gap-2">
          <button className="px-4 py-2 text-xs font-semibold rounded uppercase tracking-wider" style={{ background: p.accent, color: p.primary }}>
            Engage Us
          </button>
          <button className="px-4 py-2 text-xs font-semibold rounded uppercase tracking-wider border" style={{ borderColor: p.accent, color: p.accent, background: "transparent" }}>
            Our Services
          </button>
        </div>
      </div>

      {/* Metric strip */}
      <div className="grid grid-cols-4" style={{ background: p.secondary }}>
        {[
          { n: "7+", l: "Vendor Platforms" },
          { n: "100%", l: "Channel-Only" },
          { n: "NV", l: "Cleared" },
          { n: "AU", l: "Coverage" },
        ].map((m, i) => (
          <div key={i} className="py-3 px-2 text-center border-r" style={{ borderColor: p.primary + "40", color: p.textOnDark }}>
            <div className="text-xl font-bold" style={{ color: p.accent, fontFamily: "'JetBrains Mono', monospace" }}>{m.n}</div>
            <div className="text-[9px] uppercase tracking-wider opacity-70">{m.l}</div>
          </div>
        ))}
      </div>

      {/* Service cards */}
      <div className="px-6 py-6" style={{ background: p.surface }}>
        <div className="text-[10px] uppercase tracking-[0.25em] mb-1" style={{ color: p.muted }}>What We Do</div>
        <h2 className="text-lg font-bold mb-4" style={{ color: p.textOnSurface, fontFamily: "'Space Grotesk', sans-serif" }}>End-to-End Delivery</h2>
        <div className="grid grid-cols-2 gap-3">
          {[
            { tag: "Deployment", title: "Network Deployment" },
            { tag: "Cybersecurity", title: "Security Platforms" },
          ].map((s, i) => (
            <div key={i} className="p-3 rounded border" style={{ borderColor: p.textOnSurface + "20", background: "#FFFFFF" }}>
              <div className="text-[9px] uppercase tracking-wider mb-1" style={{ color: p.accent, fontWeight: 600 }}>{s.tag}</div>
              <div className="text-sm font-semibold mb-1" style={{ color: p.textOnSurface }}>{s.title}</div>
              <div className="text-[11px]" style={{ color: p.muted }}>End-to-end delivery for complex enterprise rollouts.</div>
              <div className="mt-2 text-[10px] uppercase tracking-wider inline-flex items-center gap-1" style={{ color: p.accent }}>Learn more →</div>
            </div>
          ))}
        </div>
      </div>

      {/* Footer strip */}
      <div className="px-6 py-3 flex items-center justify-between text-[10px]" style={{ background: p.primary, color: p.textOnDark, opacity: 0.9 }}>
        <span>© SoverenX Systems</span>
        <span className="font-mono" style={{ color: p.accent }}>soverenxsystems.com.au</span>
      </div>
    </div>
  );
}

export default function BrandPreview() {
  return (
    <div className="min-h-screen bg-slate-100 py-10 px-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-slate-500 mb-2">Brand Direction Preview</div>
          <h1 className="text-4xl font-bold text-slate-900 mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Three palettes for soverenxsystems.com.au</h1>
          <p className="text-slate-600 max-w-2xl">Each column is a mini-rendering of your actual hero copy, metric strip, and service cards using the proposed palette. Judge for vibe — ignore layout details.</p>
        </div>

        {/* Palette swatches row */}
        <div className="grid grid-cols-3 gap-6 mb-6">
          {palettes.map((p) => (
            <div key={p.id} className="bg-white rounded border border-slate-200 p-4">
              <div className="text-xs font-bold text-slate-900 mb-1">{p.name}</div>
              <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-3">{p.tag}</div>
              <div className="text-xs text-slate-600 mb-3 italic">{p.tagline}</div>
              <div className="flex gap-1">
                {[
                  { label: "Primary", c: p.primary },
                  { label: "Secondary", c: p.secondary },
                  { label: "Accent", c: p.accent },
                  { label: "Surface", c: p.surface },
                ].map((s) => (
                  <div key={s.label} className="flex-1">
                    <div className="h-10 rounded-sm border border-slate-200" style={{ background: s.c }} />
                    <div className="text-[9px] font-mono mt-1 text-slate-500">{s.label}</div>
                    <div className="text-[9px] font-mono text-slate-400">{s.c}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Mini sites row */}
        <div className="grid grid-cols-3 gap-6">
          {palettes.map((p) => (
            <MiniSite key={p.id} p={p} />
          ))}
        </div>

        <div className="mt-10 text-center text-xs text-slate-500">
          Pick one and I'll generate a full design token spec you can hand to the marketing-site builder.
        </div>
      </div>
    </div>
  );
}
